import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { DineOpsPairPanel } from './DineOpsPairPanel';
import type {
  DineCourse,
  DineMenuGroup,
  DineKitchenStation,
  DineAddonGroup,
  DineTagAnswer,
  TagAnswerInput,
} from 'api/types';

/**
 * The card's structure: courses, and the menu groups inside them (plan §5.4,
 * "Course / group modal").
 *
 * Reached from the Menu Items header rather than its own sidebar entry: a
 * course is created a handful of times in an outlet's life, a dish every week.
 *
 * WHAT A MENU GROUP CARRIES, AND WHY IT MATTERS HERE
 * The station that cooks its dishes, the minutes they take, the add-on set they
 * offer, and — the part with real consequences — its answers to the inherited
 * tag groups. A group that says "contains Dairy" says it for every dish in it
 * that has not answered for itself. That is the whole inheritance model, and
 * this dialog is where a group's half of it is entered.
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Called after any change, so the Menu Items screen can refresh its filters. */
  onChanged: () => void;
}

const DineOpsCourseGroupModal: React.FC<Props> = ({ isOpen, onClose, onChanged }) => {
  const [courses, setCourses] = useState<DineCourse[]>([]);
  const [groups, setGroups] = useState<DineMenuGroup[]>([]);
  const [stations, setStations] = useState<DineKitchenStation[]>([]);
  const [addonSets, setAddonSets] = useState<DineAddonGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  // ---- course editor
  const [courseDraft, setCourseDraft] = useState<{ id?: string; rowVersion?: number; name: string; order: string } | null>(null);

  // ---- group editor
  const [groupDraft, setGroupDraft] = useState<{
    id?: string; rowVersion?: number; isActive?: boolean;
    name: string; order: string; stationId: string; minutes: string; addonGroupId: string;
    allowFallback: boolean;
  } | null>(null);
  const [groupTags, setGroupTags] = useState<DineTagAnswer[]>([]);
  const [touchedTags, setTouchedTags] = useState<Set<string>>(new Set());

  // ------------------------------------------------------------------ loading

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, g, s, a] = await Promise.all([
        dineOpsService.getCourses({ pageSize: 100, includeInactive: true }),
        dineOpsService.getGroups({ pageSize: 200, includeInactive: true }),
        dineOpsService.getStations({ pageSize: 100 }),
        dineOpsService.getAddonGroups({ pageSize: 100 }),
      ]);
      setCourses(c.data);
      setGroups(g.data);
      setStations(s.data);
      setAddonSets(a.data);
      setSelectedCourseId((prev) => prev ?? c.data[0]?.id ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The card structure could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  const groupsInCourse = useMemo(
    () => groups.filter((g) => g.courseId === selectedCourseId).sort((a, b) => a.displayOrder - b.displayOrder),
    [groups, selectedCourseId],
  );

  const stationOptions = useMemo(
    () => stations.map((s) => ({ value: s.id, label: `${s.stationName} (${s.stationCode})` })),
    [stations],
  );
  const addonOptions = useMemo(
    () => [{ value: '', label: 'No add-on set' }, ...addonSets.map((a) => ({ value: a.id, label: a.addonGroupName }))],
    [addonSets],
  );

  const fail = (err: any, fallback: string) =>
    setError(err?.response?.data?.message ?? fallback);

  // ------------------------------------------------------------------ courses

  const saveCourse = useCallback(async () => {
    if (!courseDraft) return;
    if (!courseDraft.name.trim()) { setError('A category needs a name.'); return; }

    setSaving(true); setError(null);
    try {
      const body = { courseName: courseDraft.name.trim(), displayOrder: Number(courseDraft.order) || 0 };
      if (courseDraft.id) {
        await dineOpsService.updateCourse(courseDraft.id, { ...body, isActive: true, rowVersion: courseDraft.rowVersion! });
      } else {
        const created = await dineOpsService.createCourse(body);
        setSelectedCourseId(created.id);
      }
      setCourseDraft(null);
      await load();
      onChanged();
    } catch (err) { fail(err, 'The category could not be saved.'); }
    finally { setSaving(false); }
  }, [courseDraft, load, onChanged]);

  const deleteCourse = useCallback(async (c: DineCourse) => {
    if (c.groupCount > 0) {
      setError(`'${c.courseName}' still holds ${c.groupCount} group(s). Move or remove them first.`);
      return;
    }
    setSaving(true); setError(null);
    try {
      await dineOpsService.deleteCourse(c.id, c.rowVersion);
      if (selectedCourseId === c.id) setSelectedCourseId(null);
      await load();
      onChanged();
    } catch (err) { fail(err, 'The category could not be deleted.'); }
    finally { setSaving(false); }
  }, [selectedCourseId, load, onChanged]);

  // ------------------------------------------------------------------- groups

  const openGroup = useCallback(async (g: DineMenuGroup | null) => {
    setError(null);
    setTouchedTags(new Set());
    if (!g) {
      setGroupDraft({ name: '', order: String((groupsInCourse.length + 1) * 10), stationId: '', minutes: '', addonGroupId: '', allowFallback: true });
      setGroupTags([]);
      return;
    }
    setGroupDraft({
      id: g.id, rowVersion: g.rowVersion, isActive: g.isActive,
      name: g.groupName, order: String(g.displayOrder),
      stationId: g.stationId ?? '', minutes: g.minutesToTable != null ? String(g.minutesToTable) : '',
      addonGroupId: g.addonGroupId ?? '',
      allowFallback: g.allowFallback ?? true,
    });
    try {
      // Only inherited groups are answerable on a menu group; the API refuses
      // the rest, so they are not offered.
      const tags = await dineOpsService.getGroupTags(g.id);
      setGroupTags(tags.filter((t) => t.isInherited));
    } catch (err) { fail(err, 'The group’s tags could not be loaded.'); }
  }, [groupsInCourse.length]);

  const setGroupAnswer = useCallback((tagGroupId: string, tagIds: string[]) => {
    setTouchedTags((prev) => new Set(prev).add(tagGroupId));
    setGroupTags((prev) => prev.map((a) => {
      if (a.tagGroupId !== tagGroupId) return a;
      const chosen = a.options.filter((o) => tagIds.includes(o.id));
      return { ...a, answers: chosen, source: chosen.length ? 'Own' : 'Unanswered' };
    }));
  }, []);

  const saveGroup = useCallback(async () => {
    if (!groupDraft || !selectedCourseId) return;
    if (!groupDraft.name.trim()) { setError('A menu group needs a name.'); return; }
    // BR-054: the group's station is where every dish goes unless it overrides.
    if (!groupDraft.stationId) { setError('A menu group needs a kitchen station.'); return; }
    if (groupDraft.minutes.trim() && !(Number(groupDraft.minutes) > 0)) {
      setError('Time to table is a number of minutes.'); return;
    }

    setSaving(true); setError(null);
    try {
      const body = {
        courseId: selectedCourseId,
        groupName: groupDraft.name.trim(),
        displayOrder: Number(groupDraft.order) || 0,
        stationId: groupDraft.stationId || undefined,
        minutesToTable: groupDraft.minutes.trim() ? Number(groupDraft.minutes) : undefined,
        addonGroupId: groupDraft.addonGroupId || undefined,
        allowFallback: groupDraft.allowFallback,
      };

      let id = groupDraft.id;
      if (id) {
        await dineOpsService.updateGroup(id, { ...body, isActive: groupDraft.isActive ?? true, rowVersion: groupDraft.rowVersion! });
      } else {
        id = (await dineOpsService.createGroup(body)).id;
      }

      /*
       * Only touched tag groups are sent — the same rule as the item dialog.
       * A menu group has nothing above it to inherit from, so the risk there is
       * smaller, but sending everything would still turn "unanswered" into an
       * explicit empty answer on every save.
       */
      const answers: TagAnswerInput[] = groupTags
        .filter((t) => touchedTags.has(t.tagGroupId))
        .map((t) => ({ tagGroupId: t.tagGroupId, tagIds: t.answers.map((x) => x.id), isNone: false }));
      if (answers.length > 0) await dineOpsService.saveGroupTags(id, answers);

      notificationService.success('Saved', `'${body.groupName}' saved${answers.length ? ` with ${answers.length} tag answer(s)` : ''}.`);
      setGroupDraft(null);
      await load();
      onChanged();
    } catch (err) { fail(err, 'The menu group could not be saved.'); }
    finally { setSaving(false); }
  }, [groupDraft, selectedCourseId, groupTags, touchedTags, load, onChanged]);

  const deleteGroup = useCallback(async (g: DineMenuGroup) => {
    if (g.itemCount > 0) {
      setError(`'${g.groupName}' still holds ${g.itemCount} dish(es). Move or remove them first.`);
      return;
    }
    setSaving(true); setError(null);
    try {
      await dineOpsService.deleteGroup(g.id, g.rowVersion);
      await load();
      onChanged();
    } catch (err) { fail(err, 'The menu group could not be deleted.'); }
    finally { setSaving(false); }
  }, [load, onChanged]);

  if (!isOpen) return null;

  const selectedCourse = courses.find((c) => c.id === selectedCourseId) ?? null;

  // --------------------------------------------------------------------- render

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Categories &amp; menu groups</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={18} />
          </button>
        </div>

        <div className="modal-content" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}
          {loading && <div style={{ padding: 16 }}>Loading…</div>}

          {!loading && (
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20, minHeight: 320 }}>
              {/* ---------------------------------------------------- courses */}
              <div style={{ borderRight: '1px solid var(--border-color, #e3e8ef)', paddingRight: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong>Categories</strong>
                  <button className="btn btn-secondary" style={{ padding: '2px 8px' }}
                    onClick={() => setCourseDraft({ name: '', order: String((courses.length + 1) * 10) })} title="Add a category">
                    <IconSystem.add size={14} />
                  </button>
                </div>

                {courses.length === 0 && <div style={{ opacity: 0.6, fontSize: 13 }}>No categories yet. Add one.</div>}

                {courses.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => { setSelectedCourseId(c.id); setGroupDraft(null); }}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '6px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 2,
                      background: c.id === selectedCourseId ? 'var(--bg-secondary, #eef2f7)' : 'transparent',
                      opacity: c.isActive ? 1 : 0.55,
                    }}
                  >
                    <span>{c.courseName} <span style={{ opacity: 0.55, fontSize: 12 }}>({c.groupCount})</span></span>
                    <span style={{ display: 'flex', gap: 4 }}>
                      <button className="modal-header-btn" title="Rename"
                        onClick={(e) => { e.stopPropagation(); setCourseDraft({ id: c.id, rowVersion: c.rowVersion, name: c.courseName, order: String(c.displayOrder) }); }}>
                        <IconSystem.edit size={13} />
                      </button>
                      <button className="modal-header-btn" title={c.groupCount ? 'Has groups — cannot delete' : 'Delete'}
                        disabled={c.groupCount > 0} style={c.groupCount ? { opacity: 0.35 } : undefined}
                        onClick={(e) => { e.stopPropagation(); void deleteCourse(c); }}>
                        <IconSystem.trash size={13} />
                      </button>
                    </span>
                  </div>
                ))}

                {courseDraft && (
                  <div style={{ marginTop: 10, padding: 10, border: '1px solid var(--border-color, #e3e8ef)', borderRadius: 6 }}>
                    <div className="form-group" style={{ marginBottom: 6 }}>
                      <label className="form-label">Category name</label>
                      <input className="form-input" autoFocus value={courseDraft.name}
                        onChange={(e) => setCourseDraft((p) => p && ({ ...p, name: e.target.value }))} placeholder="e.g., Starters" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label className="form-label">Order</label>
                      <input className="form-input" type="number" value={courseDraft.order}
                        onChange={(e) => setCourseDraft((p) => p && ({ ...p, order: e.target.value }))} />
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-primary" style={{ padding: '4px 10px' }} onClick={() => void saveCourse()} disabled={saving}>Save</button>
                      <button className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => setCourseDraft(null)} disabled={saving}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>

              {/* ----------------------------------------------------- groups */}
              <div>
                {!selectedCourse && <div style={{ opacity: 0.6 }}>Choose a category on the left.</div>}

                {selectedCourse && !groupDraft && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <strong>Groups in {selectedCourse.courseName}</strong>
                      <button className="btn btn-secondary" style={{ padding: '2px 8px' }} onClick={() => void openGroup(null)}>
                        <IconSystem.add size={14} /> Add a group
                      </button>
                    </div>

                    {groupsInCourse.length === 0 && (
                      <div style={{ opacity: 0.6, fontSize: 13 }}>No groups in this category yet. Dishes live in groups, so add one first.</div>
                    )}

                    {groupsInCourse.map((g) => (
                      <div key={g.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 10px', border: '1px solid var(--border-color, #e3e8ef)', borderRadius: 6, marginBottom: 6,
                        opacity: g.isActive ? 1 : 0.55,
                      }}>
                        <div>
                          <div><strong>{g.groupName}</strong> <span style={{ opacity: 0.55, fontSize: 12 }}>· {g.itemCount} dish(es)</span></div>
                          <div style={{ fontSize: 12, opacity: 0.7 }}>
                            {g.stationName ?? 'no kitchen station'}
                            {g.minutesToTable ? ` · ${g.minutesToTable} min` : ''}
                            {g.addonGroupName ? ` · extras: ${g.addonGroupName}` : ''}
                            {g.inheritedTagCount ? ` · ${g.inheritedTagCount} tag answer(s) handed down` : ''}
                          </div>
                        </div>
                        <span style={{ display: 'flex', gap: 4 }}>
                          <button className="modal-header-btn" title="Edit" onClick={() => void openGroup(g)}><IconSystem.edit size={14} /></button>
                          <button className="modal-header-btn" title={g.itemCount ? 'Has dishes — cannot delete' : 'Delete'}
                            disabled={g.itemCount > 0} style={g.itemCount ? { opacity: 0.35 } : undefined}
                            onClick={() => void deleteGroup(g)}><IconSystem.trash size={14} /></button>
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {selectedCourse && groupDraft && (
                  <>
                    <strong>{groupDraft.id ? 'Edit group' : 'New group'} in {selectedCourse.courseName}</strong>

                    <div className="form-grid-2" style={{ marginTop: 10 }}>
                      <div className="form-group">
                        <label className="form-label">Group name <span className="text-danger">*</span></label>
                        <input className="form-input" autoFocus value={groupDraft.name}
                          onChange={(e) => setGroupDraft((p) => p && ({ ...p, name: e.target.value }))} placeholder="e.g., Tandoori breads" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Order</label>
                        <input className="form-input" type="number" value={groupDraft.order}
                          onChange={(e) => setGroupDraft((p) => p && ({ ...p, order: e.target.value }))} />
                      </div>
                    </div>

                    <div className="form-grid-2">
                      <div className="form-group">
                        <label className="form-label">Kitchen station <span className="text-danger">*</span></label>
                        <SyutSelect options={stationOptions} value={groupDraft.stationId} size="sm" placeholder="Choose a kitchen station"
                          onChange={(v) => setGroupDraft((p) => p && ({ ...p, stationId: (v as string) || '' }))} />
                        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>Where this group&rsquo;s tickets go. A dish inherits it.</div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Time to table (minutes)</label>
                        <input className="form-input" type="number" min={1} value={groupDraft.minutes}
                          onChange={(e) => setGroupDraft((p) => p && ({ ...p, minutes: e.target.value }))} placeholder="e.g., 12" />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Add-on set</label>
                      <SyutSelect options={addonOptions} value={groupDraft.addonGroupId} size="sm" placeholder="No add-on set"
                        onChange={(v) => setGroupDraft((p) => p && ({ ...p, addonGroupId: (v as string) || '' }))} />
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>The extras every dish in this group offers, unless a dish declares its own.</div>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={groupDraft.allowFallback}
                          onChange={(e) => setGroupDraft((p) => p && ({ ...p, allowFallback: e.target.checked }))} />
                        <span>Can go to the fallback station when its kitchen station is closed</span>
                      </label>
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                        Untick for dishes another kitchen cannot make, such as shakes that need the bar&rsquo;s blender.
                        A dish can still answer for itself.
                      </div>
                    </div>

                    {groupDraft.id && (
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={groupDraft.isActive ?? true}
                            onChange={(e) => setGroupDraft((p) => p && ({ ...p, isActive: e.target.checked }))} />
                          <span>Active</span>
                        </label>
                      </div>
                    )}

                    {/* ------------------------------------------ inherited tags */}
                    {groupDraft.id && groupTags.length > 0 && (
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-color, #e3e8ef)' }}>
                        <strong>What this group hands down to its dishes</strong>
                        <div style={{ fontSize: 12, opacity: 0.7, margin: '4px 0 10px' }}>
                          Every dish in the group inherits these answers unless it answers for itself, or
                          says &ldquo;none&rdquo; on purpose.
                        </div>
                        {groupTags.map((a) => (
                          <div key={a.tagGroupId} className="form-group" style={{ paddingBottom: 8, borderBottom: '1px solid var(--border-color, #f0f0f0)' }}>
                            <label className="form-label">
                              {a.tagGroupName}
                              {a.isDanger && <span style={{ color: 'var(--color-danger, #c0392b)', marginLeft: 6 }}>allergen</span>}
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                              {a.options.map((opt) => {
                                const chosen = a.answers.some((x) => x.id === opt.id);
                                return (
                                  <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <input
                                      type={a.selectMode === 'ONE' ? 'radio' : 'checkbox'}
                                      name={`gt-${a.tagGroupId}`}
                                      checked={chosen}
                                      onChange={(e) => {
                                        if (a.selectMode === 'ONE') return setGroupAnswer(a.tagGroupId, [opt.id]);
                                        const ids = a.answers.map((x) => x.id);
                                        setGroupAnswer(a.tagGroupId, e.target.checked ? [...ids, opt.id] : ids.filter((i) => i !== opt.id));
                                      }}
                                    />
                                    <span>{opt.tagName}</span>
                                  </label>
                                );
                              })}
                              {a.answers.length > 0 && (
                                <button className="btn btn-secondary" style={{ padding: '0 8px', marginLeft: 'auto', fontSize: 12 }}
                                  onClick={() => setGroupAnswer(a.tagGroupId, [])}>clear</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ------------------------------------------ goes well with (8.7) */}
                    {groupDraft.id && (
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-color, #e3e8ef)' }}>
                        <strong>Goes well with</strong>
                        <div style={{ marginTop: 6 }}>
                          <DineOpsPairPanel groupId={groupDraft.id} onChanged={onChanged} />
                        </div>
                      </div>
                    )}

                    {!groupDraft.id && (
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
                        Save the group, then reopen it to set what it hands down to its dishes and what goes well with them.
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                      <button className="btn btn-primary" onClick={() => void saveGroup()} disabled={saving}>{saving ? 'Saving…' : 'Save group'}</button>
                      <button className="btn btn-secondary" onClick={() => setGroupDraft(null)} disabled={saving}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
        </div>
      </div>
    </>
  );
};

export default DineOpsCourseGroupModal;
